import { fetchAllProjects } from "../../api";
import { fetchRecentlyViewedProjectIds, fetchPinnedProjectIds } from "../../api";
import { Project } from "../../interfaces/Project";
import Logger from "../../clients/sentry";
import { handleError } from "../../errors";

export function useProjectActions() {
  const getAllProjects = async (): Promise<Project[]> => {
    const result = await fetchAllProjects();
    if (!result.success) {
      console.error("Error fetching all projects", result.error);
      return [];
    }
    // Sort by updated_at descending (most recently updated first)
    const sortedProjects = [...(result.data || [])].sort((a, b) => {
      const dateA = new Date(a.updated_at).getTime();
      const dateB = new Date(b.updated_at).getTime();
      return dateB - dateA; // Descending order
    });
    return sortedProjects;
  };

  const getRecentlyViewedProjectIds = async (): Promise<string[]> => {
    const result = await fetchRecentlyViewedProjectIds();
    if (!result.success) {
      console.error("Error fetching recently viewed projects", result.error);
      return [];
    }
    return Array.isArray(result.data) ? result.data : [];
  };

  const getPinnedProjectIds = async (): Promise<string[]> => {
    const result = await fetchPinnedProjectIds();
    if (!result.success) {
      console.error("Error fetching pinned projects", result.error);
      return [];
    }
    return Array.isArray(result.data) ? result.data : [];
  };

  const getAllProjectData = async (): Promise<{
    projects: Project[];
    recentlyViewedProjectIds: string[];
    pinnedProjectIds: string[];
  }> => {
    try {
      const projects = await getAllProjects();
      const recentlyViewedProjectIds = await getRecentlyViewedProjectIds();
      const pinnedProjectIds = await getPinnedProjectIds();
      return {
        projects,
        recentlyViewedProjectIds,
        pinnedProjectIds,
      };
    } catch (error) {
      const err = handleError(error, { context: "getAllProjectData" });
      Logger.error("Get all project data failed", err);
      throw err;
    }
  };

  return {
    getAllProjects,
    getRecentlyViewedProjectIds,
    getPinnedProjectIds,
    getAllProjectData,
  };
}
